import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export interface SharingGroup {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  member_count: number;
  members: GroupMember[];
}

export interface GroupMember {
  id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  added_at: string;
}

export function useGroupsManagement() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Buscar grupos do usuário
  const { data: groups, isLoading } = useQuery({
    queryKey: ['sharing-groups', user?.id],
    queryFn: async (): Promise<SharingGroup[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data: groupsData, error } = await supabase
        .from('sharing_groups')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Para cada grupo, buscar membros
      const groupsWithMembers = await Promise.all(
        (groupsData || []).map(async (group) => {
          // Buscar membros do grupo
          const { data: membersData } = await supabase
            .from('sharing_group_members')
            .select('id, user_id, added_at')
            .eq('group_id', group.id);

          // Para cada membro, buscar dados do usuário
          const members: GroupMember[] = await Promise.all(
            (membersData || []).map(async (m) => {
              const { data: userInfo } = await supabase.rpc('get_user_info', {
                target_user_id: m.user_id
              });

              const userData = Array.isArray(userInfo) ? userInfo[0] : userInfo;

              return {
                id: m.id,
                user_id: m.user_id,
                email: (userData as any)?.email || '',
                display_name: (userData as any)?.display_name || null,
                avatar_url: (userData as any)?.avatar_url || null,
                added_at: m.added_at,
              };
            })
          );

          return {
            ...group,
            member_count: members.length,
            members,
          };
        })
      );

      return groupsWithMembers;
    },
    enabled: !!user,
  });

  // Criar grupo
  const createGroup = useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('sharing_groups')
        .insert({
          user_id: user.id,
          name,
          description: description || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-groups'] });
      toast.success('Grupo criado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao criar grupo');
    },
  });

  // Atualizar grupo
  const updateGroup = useMutation({
    mutationFn: async ({ 
      groupId, 
      name, 
      description 
    }: { 
      groupId: string; 
      name: string; 
      description?: string;
    }) => {
      const { error } = await supabase
        .from('sharing_groups')
        .update({
          name,
          description: description || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', groupId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-groups'] });
      toast.success('Grupo atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar grupo');
    },
  });

  // Deletar grupo
  const deleteGroup = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await supabase
        .from('sharing_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-groups'] });
      toast.success('Grupo excluído com sucesso');
    },
    onError: () => {
      toast.error('Erro ao excluir grupo');
    },
  });

  // Adicionar membro ao grupo
  const addMember = useMutation({
    mutationFn: async ({ groupId, userIds }: { groupId: string; userIds: string[] }) => {
      if (!user) throw new Error('User not authenticated');

      const inserts = userIds.map((userId) => ({
        group_id: groupId,
        user_id: userId,
        added_by: user.id,
      }));

      const { error } = await supabase
        .from('sharing_group_members')
        .insert(inserts);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-groups'] });
      toast.success('Membros adicionados ao grupo!');
    },
    onError: (error: any) => {
      if (error.message?.includes('duplicate')) {
        toast.error('Um ou mais usuários já estão no grupo');
      } else {
        toast.error('Erro ao adicionar membros');
      }
    },
  });

  // Remover membro do grupo
  const removeMember = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      const { error } = await supabase
        .from('sharing_group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-groups'] });
      toast.success('Membro removido do grupo');
    },
    onError: () => {
      toast.error('Erro ao remover membro');
    },
  });

  return {
    groups,
    isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
    addMember,
    removeMember,
  };
}
